import { motion } from 'framer-motion'
import { ArrowDownRight, ArrowUpRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useEffect, useMemo, useState } from 'react'
import { reportsApi } from '@/services/api'
import { useAuthStore } from '@/stores/authStore'
import { formatCurrency } from '@/lib/utils'
import type { Transaction } from '@/types'

interface TransfersChartProps {
  period?: string
}

interface TransferTotals {
  sentAmount: number
  sentCount: number
  receivedAmount: number
  receivedCount: number
}

const getPeriodRange = (period: string) => {
  const end = new Date()
  const start = new Date(end)
  if (period === 'week') {
    start.setDate(end.getDate() - 7)
  } else if (period === 'year') {
    start.setDate(end.getDate() - 365)
  } else {
    start.setDate(end.getDate() - 30)
  }
  return { start, end }
}

export function TransfersChart({ period = 'month' }: TransfersChartProps) {
  const { t } = useTranslation()
  const { user } = useAuthStore()
  const [totals, setTotals] = useState<TransferTotals>({
    sentAmount: 0,
    sentCount: 0,
    receivedAmount: 0,
    receivedCount: 0,
  })
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const loadTransfers = async () => {
      if (!user?.email) {
        setTotals({ sentAmount: 0, sentCount: 0, receivedAmount: 0, receivedCount: 0 })
        setIsLoading(false)
        return
      }

      setIsLoading(true)
      try {
        const { start, end } = getPeriodRange(period)
        const response = await reportsApi.getTransactionHistory({
          start_date: start.toISOString(),
          end_date: end.toISOString(),
        })
        const payload = response.data.data || response.data
        const transactions: Transaction[] = Array.isArray(payload) ? payload : []

        const transferTransactions = transactions.filter(
          (transaction) => transaction.transaction_type === 'P2P'
        )

        let sentAmount = 0
        let sentCount = 0
        let receivedAmount = 0
        let receivedCount = 0

        transferTransactions.forEach((transaction) => {
          if (transaction.from_wallet_user === user.email) {
            sentAmount += Number(transaction.amount || 0)
            sentCount += 1
          } else if (transaction.to_wallet_user === user.email) {
            receivedAmount += Number(transaction.amount || 0)
            receivedCount += 1
          }
        })

        setTotals({
          sentAmount,
          sentCount,
          receivedAmount,
          receivedCount,
        })
      } catch (error) {
        console.error('Error loading transfer data:', error)
        setTotals({ sentAmount: 0, sentCount: 0, receivedAmount: 0, receivedCount: 0 })
      } finally {
        setIsLoading(false)
      }
    }

    loadTransfers()
  }, [period, user?.email])

  const maxAmount = useMemo(() => {
    return Math.max(totals.sentAmount, totals.receivedAmount, 0)
  }, [totals])

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.5 }}
      className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mt-4"
    >
      <h2 className="text-lg font-semibold text-text-primary mb-4">
        {t('reports.transfersTitle')}
      </h2>

      {isLoading ? (
        <div className="h-32 bg-gray-100 rounded-lg flex items-center justify-center">
          <p className="text-text-secondary">{t('common.loading')}</p>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="space-y-3">
            {[
              {
                key: 'sent',
                label: t('reports.transfersSent'),
                icon: ArrowUpRight,
                amount: totals.sentAmount,
                count: totals.sentCount,
                color: 'bg-error',
              },
              {
                key: 'received',
                label: t('reports.transfersReceived'),
                icon: ArrowDownRight,
                amount: totals.receivedAmount,
                count: totals.receivedCount,
                color: 'bg-success',
              },
            ].map((item) => {
              const height = maxAmount > 0 ? (item.amount / maxAmount) * 100 : 0
              const Icon = item.icon
              return (
                <div key={item.key} className="flex items-center gap-4">
                  <div className={`h-10 w-10 rounded-lg ${item.color}/10 flex items-center justify-center`}>
                    <Icon className={`h-5 w-5 ${item.color.replace('bg-', 'text-')}`} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium text-text-primary">{item.label}</span>
                      <span className="text-text-secondary">
                        {t('reports.transfersCount', { count: item.count })}
                      </span>
                    </div>
                    <div className="mt-2 h-2 w-full rounded-full bg-gray-200">
                      <div
                        className={`h-2 rounded-full ${item.color}`}
                        style={{ width: `${height}%` }}
                      />
                    </div>
                    <div className="mt-2 text-sm font-semibold text-text-primary">
                      {formatCurrency(item.amount, 'STN')}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </motion.div>
  )
}
